import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance.js';
import { Drawer } from "@mantine/core";
import { renderArtUserActionButtons } from './artUserActionButtons';
import { tagName, isGlobalManualTag } from './TagBadge';

const FollowingDropdown = ({
  isOpen,
  onClose,
  language,
  art,
  otherPeopleEvents = [],
  recommendationsFromMeMap,
  recommendationsToMeMap,
  messagesSentToUser,
  sendingToUser,
  handleRecommend,
  navigateToDiscussion,
  sendArtLinkToUser,
  goToDirectChat,
  hasEvent,
  hasRecommendationFromMe,
  hasRecommendationToMe,
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const navigate = useNavigate();

  // Per-user tag-name lookup, derived from the parent-provided list.
  const tagNamesByEventId = {};
  const userIdsWithEvent = new Set();
  for (const ev of otherPeopleEvents) {
    tagNamesByEventId[ev.artEventId] = ev.eventTagNames || [];
    userIdsWithEvent.add(ev.userId);
  }

  // Surface users who already have an event for this art at the top.
  const sortedUsers = [...users].sort((a, b) => {
    const aHas = userIdsWithEvent.has(a.id) ? 1 : 0;
    const bHas = userIdsWithEvent.has(b.id) ? 1 : 0;
    return bHas - aHas;
  });

  useEffect(() => {
    if (!isOpen) return;
    axiosInstance
      .get('/getUsers?onlyMyFollowersOrFollowees=true')
      .then((response) => setUsers(response.data))
      .catch((error) => console.error('Failed to fetch users:', error));
  }, [isOpen]);

  const toggleUserSelection = (userId) => {
    if (hasEvent(userId)) {
      // If the new user has an event without a "todo" tag, unselect all other users
      if (selectedUsers.length == 1 && selectedUsers[0] === userId) {
        // If the user is already selected, unselect them
        setSelectedUsers([]);
      } else {
        // Unselect all other users
        setSelectedUsers([userId]);
      }
    } else { // userId can be recommended to
      setSelectedUsers((prevSelected) => {
        const userWithEvent = prevSelected.find(
          (userId) =>
            hasEvent(userId) ||
            hasRecommendationFromMe(userId) ||
            hasRecommendationToMe(userId)
        );
        if (userWithEvent) { // there was someone who can't be recommended to
          return [userId];// prevSelected.filter((id) => id !== userWithEventWithoutTodo && id !== userId);
        }
        // Add userId to selectedUsers if not already selected
        return prevSelected.includes(userId)
          ? prevSelected.filter((id) => id !== userId)
          : [...prevSelected, userId];
      });
    }
  };

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      position="bottom"
      size="75vh"
      radius="md"
      padding="md"
      lockScroll={false}
      classNames={{ root: 'art-page-drawer' }}
      title={<span className="font-bold">{language === "ru" ? "Взаимодействие с друзьями" : "Interaction with friends"}</span>}
      styles={{
        header: { paddingBottom: 0 },
        title: { flex: 1, textAlign: 'center' },
        body: { height: 'calc(100% - 50px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      }}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-3 gap-4 p-4">
          {(() => {
            // Chunk into rows of 3 so we can inject a full-width action
            // strip right below the row containing the selected user
            // (or the LOWEST row containing any selected user when
            // multiple are selected).
            const rows = [];
            for (let i = 0; i < sortedUsers.length; i += 3) {
              rows.push(sortedUsers.slice(i, i + 3));
            }
            const stripButtons = renderArtUserActionButtons({
              selectedUsers,
              language,
              hasEvent,
              hasRecommendationFromMe,
              hasRecommendationToMe,
              recommendationsFromMeMap,
              recommendationsToMeMap,
              messagesSentToUser,
              sendingToUser,
              navigate,
              handleRecommend,
              navigateToDiscussion,
              sendArtLinkToUser,
              goToDirectChat,
              getUserName: (uid) => sortedUsers.find((u) => u.id === uid)?.userName,
            });
            let stripRowIdx = -1;
            let stripCol = 0; // 0..2 — which column the single-cell strip sits under
            if (stripButtons.length > 0) {
              if (selectedUsers.length === 1) {
                const uid = selectedUsers[0];
                for (let r = 0; r < rows.length; r++) {
                  const idx = rows[r].findIndex((u) => u.id === uid);
                  if (idx !== -1) { stripRowIdx = r; stripCol = idx; break; }
                }
              } else {
                // Lowest row containing any selected user, aligned under
                // the leftmost selected user in that row.
                for (let r = 0; r < rows.length; r++) {
                  const idx = rows[r].findIndex((u) => selectedUsers.includes(u.id));
                  if (idx !== -1) { stripRowIdx = r; stripCol = idx; }
                }
              }
            }
            const out = [];
            rows.forEach((row, rIdx) => {
              row.forEach((user) => {
                const eventTagNames = tagNamesByEventId[otherPeopleEvents.find(e => e.userId === user.id)?.artEventId] || [];
                const tagIconName = eventTagNames.find(isGlobalManualTag);
                const tagIcon = tagIconName ? tagName(tagIconName, language, undefined, true) : null;
                out.push(
                  <div
                    key={user.id}
                    className="text-center cursor-pointer"
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <div
                      className="w-16 h-16 rounded-full mx-auto border-4 relative"
                      style={{ borderColor: selectedUsers.includes(user.id) ? 'var(--mantine-primary-color-filled)' : 'transparent' }}
                    >
                      <img
                        src={user.avatarUrl}
                        alt={user.userName}
                        className="w-full h-full rounded-full"
                      />
                      {tagIcon && (
                        <span
                          className="absolute pointer-events-none flex items-center justify-center"
                          style={{
                            right: -4,
                            bottom: -4,
                            lineHeight: 1,
                            fontSize: 18,
                            textShadow: '0 0 3px var(--mantine-color-body), 0 0 3px var(--mantine-color-body)',
                          }}
                        >
                          {tagIcon}
                        </span>
                      )}
                    </div>
                    <p className="text-sm mt-2">
                      {user.userName.length > 10 ? `${user.userName.slice(0, 10)}...` : user.userName}
                    </p>
                    {recommendationsFromMeMap[user.id] && (
                      <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-blue-text)' }}>
                        {language === "ru" ? "Вы рекомендовали" : "You recommended"}
                      </p>
                    )}
                    {recommendationsToMeMap[user.id] && (
                      <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-green-text)' }}>
                        {language === "ru" ? "Вам рекомендовали" : "Recommended to you"}
                      </p>
                    )}
                  </div>
                );
              });
              if (rIdx === stripRowIdx) {
                out.push(
                  <div
                    key={`action-strip-${rIdx}`}
                    className="col-span-3 grid grid-cols-3 gap-4"
                  >
                    <div
                      className={`flex flex-col gap-1.5 -mt-2 w-[172.5%] sm:w-[115%] ${
                        stripCol === 1
                          ? '-ml-[36.25%] sm:-ml-[7.5%]'
                          : stripCol === 2
                            ? '-ml-[72.5%] sm:-ml-[15%]'
                            : ''
                      }`}
                      style={{ gridColumnStart: stripCol + 1 }}
                    >
                      {stripButtons}
                    </div>
                  </div>
                );
              }
            });
            return out;
          })()}
        </div>
      </div>
    </Drawer>
  );
};

export default FollowingDropdown;
